/**
 * User Roles Configuration
 * 
 * Defines the three user roles in the system:
 * - ADMIN: Full access to everything
 * - TEACHER: Can create courses, quizzes, manage students
 * - STUDENT: Can join courses, take quizzes, view own stats
 */

// User role constants
export const USER_ROLES = {
  ADMIN: 'admin',
  TEACHER: 'teacher',
  STUDENT: 'student'
};

// Role display names (German)
export const ROLE_DISPLAY_NAMES = {
  [USER_ROLES.ADMIN]: 'Administrator',
  [USER_ROLES.TEACHER]: 'Lehrende/r',
  [USER_ROLES.STUDENT]: 'Studierende/r'
};

// Role colors for UI
export const ROLE_COLORS = {
  [USER_ROLES.ADMIN]: {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-300',
    badge: 'bg-purple-600'
  },
  [USER_ROLES.TEACHER]: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-300',
    badge: 'bg-blue-600'
  },
  [USER_ROLES.STUDENT]: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300',
    badge: 'bg-green-600'
  }
};

// Permission definitions
export const PERMISSIONS = {
  // Admin-only permissions
  MANAGE_USERS: 'manage_users',
  MANAGE_ALL_COURSES: 'manage_all_courses',
  VIEW_SYSTEM_STATS: 'view_system_stats',
  MANAGE_SETTINGS: 'manage_settings',
  
  // Teacher permissions
  CREATE_COURSE: 'create_course',
  MANAGE_OWN_COURSES: 'manage_own_courses',
  CREATE_QUIZ: 'create_quiz',
  RUN_LIVE_SESSION: 'run_live_session',
  VIEW_STUDENT_STATS: 'view_student_stats',
  INVITE_STUDENTS: 'invite_students',
  USE_LLM_GENERATOR: 'use_llm_generator',
  
  // Student permissions
  JOIN_COURSE: 'join_course',
  TAKE_QUIZ: 'take_quiz',
  VIEW_OWN_STATS: 'view_own_stats',
  PRACTICE_QUIZ: 'practice_quiz'
};

// Role-permission mapping
export const ROLE_PERMISSIONS = {
  [USER_ROLES.ADMIN]: [
    // Admin has all permissions
    ...Object.values(PERMISSIONS)
  ],
  [USER_ROLES.TEACHER]: [
    PERMISSIONS.CREATE_COURSE,
    PERMISSIONS.MANAGE_OWN_COURSES,
    PERMISSIONS.CREATE_QUIZ,
    PERMISSIONS.RUN_LIVE_SESSION,
    PERMISSIONS.VIEW_STUDENT_STATS,
    PERMISSIONS.INVITE_STUDENTS,
    PERMISSIONS.USE_LLM_GENERATOR,
    // Teachers can also do student things
    PERMISSIONS.JOIN_COURSE,
    PERMISSIONS.TAKE_QUIZ,
    PERMISSIONS.VIEW_OWN_STATS,
    PERMISSIONS.PRACTICE_QUIZ
  ],
  [USER_ROLES.STUDENT]: [
    PERMISSIONS.JOIN_COURSE,
    PERMISSIONS.TAKE_QUIZ,
    PERMISSIONS.VIEW_OWN_STATS,
    PERMISSIONS.PRACTICE_QUIZ
  ]
};

/**
 * Check if a role has a specific permission
 * @param {string} role - User role
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export function hasPermission(role, permission) {
  const rolePermissions = ROLE_PERMISSIONS[role];
  if (!rolePermissions) return false;
  return rolePermissions.includes(permission);
}

/**
 * Check if user is admin
 * @param {string} role - User role
 * @returns {boolean}
 */
export function isAdmin(role) {
  return role === USER_ROLES.ADMIN;
}

/**
 * Check if user is teacher or admin
 * @param {string} role - User role
 * @returns {boolean}
 */
export function isTeacherOrAdmin(role) {
  return role === USER_ROLES.ADMIN || role === USER_ROLES.TEACHER;
}

/**
 * Get role display name
 * @param {string} role - User role
 * @returns {string}
 */
export function getRoleDisplayName(role) {
  return ROLE_DISPLAY_NAMES[role] || 'Unbekannt';
}

/**
 * Get role colors
 * @param {string} role - User role
 * @returns {Object}
 */
export function getRoleColors(role) {
  return ROLE_COLORS[role] || ROLE_COLORS[USER_ROLES.STUDENT];
}

export default {
  USER_ROLES,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  hasPermission,
  isAdmin,
  isTeacherOrAdmin,
  getRoleDisplayName,
  getRoleColors
};
